import { inject, Injectable } from '@angular/core';
import { RxStomp, RxStompState } from '@stomp/rx-stomp';
import { environment } from '../../environments/environment';
import { filter, from, map, mergeMap } from 'rxjs';
import { JsonUtils } from '../utils/json.utils';
import { BrowserSessionService } from './browser-session.service';
import { AuthenticationService } from './authentication.service';

@Injectable({ providedIn: 'root' })
export class RealTimeService {

  private _rxStomp: RxStomp;
  private _isRealTimeInit$: Promise<void>;

  private readonly browserSessionService: BrowserSessionService;
  private readonly authenticationService: AuthenticationService;

  constructor() {
    this.browserSessionService = inject(BrowserSessionService);
    this.authenticationService = inject(AuthenticationService);
    this.initRealtime(this.browserSessionService.sessionId);
  }

  public sendDataOnStomp(data: unknown, destination: string) {
    this._rxStomp.publish({ destination: `/${destination}`, body: JSON.stringify(data) });
  }

  public watch<T>(destination: string, prefix: string = '') {
    return from(this.getStompConnection())
      .pipe(
        mergeMap(() => this._rxStomp.watch(`${prefix}/topic/${destination}`)),
        map(message => JsonUtils.parseJsonIfValid(message.body, message.body) as T)
      );

  }

  public isConnected$() {
    return from(this.getStompConnection()).pipe(
      mergeMap(rxStomp => rxStomp.connectionState$),
      filter(state => state === RxStompState.OPEN)
    );
  }

  public terminateConnection(): void {
    this._rxStomp.deactivate({ force: true }).then(() => {
      this._rxStomp = undefined;
    });
  }

  private async getStompConnection() {
    await this._isRealTimeInit$;
    return this._rxStomp;
  }

  private initRealtime(sessionId: string): void {
    this._isRealTimeInit$ = new Promise((resolve) => {
      if (this.authenticationService.tokenNeedsRefreshing()) {
        this.authenticationService.refreshAuthentication$().subscribe({
          next: () => {
            this.initRxStompConnexion(sessionId, this.authenticationService.getAccessToken(), true);
            resolve();
          },
        });
      } else {
        this.initRxStompConnexion(sessionId, this.authenticationService.getAccessToken());
        resolve();
      }
    });
  }

  private initRxStompConnexion(sessionId: string, authenticationToken: string, forceRefreshToken = false): void {
    if (this._rxStomp?.active && !forceRefreshToken) {
      return;
    }
    if (this._rxStomp?.active && forceRefreshToken) {
      this._rxStomp.deactivate({ force: true }).then(() => {
        this._rxStomp = this.createRxStompConnection(sessionId, authenticationToken);
      });
    }
    if (!this._rxStomp) {
      this._rxStomp = this.createRxStompConnection(sessionId, authenticationToken);
      this._rxStomp.activate();
    }
  }

  private createRxStompConnection(sessionId: string, authenticationToken: string) {
    const rxStomp = new RxStomp();
    rxStomp.configure({
      connectHeaders: { 'browser-session-id': sessionId },
      brokerURL: `${environment.broker_api_base_url}real-time?token=${authenticationToken}`,
      reconnectDelay: 2000,
      debug: (msg: string): void => (environment.log.websocket ? console.log(new Date(), msg) : null)
    });
    return rxStomp;
  }

}
